import { IMemberWithBasicProfile } from "constants/merchants";
import _ from "lodash/fp";
import { format } from "mathjs";
import { combineReducers } from "redux";
import { Action, ActionMeta, handleActions } from "redux-actions";
import { MERCHANT_ADMIN_MEMBER_LIST } from "../constants/action-types";
import { IBaseStoreState, IFetchStoreState, progressActions, updateActions } from "./base-reducers";
export interface IMerchantAdminMemberListState {
    members: IMembersState;
    member: IFetchStoreState<unknown>;
    successMessage: string;
    errorMessage: string;
    addingAdminMembers: boolean;
}

export interface IMembersPayload {
    members: IMemberWithBasicProfile[];
    next_page_token: string;
}

export interface IMembersState extends IFetchStoreState<IMembersPayload> {}
export interface IMemberState extends IBaseStoreState<unknown> {
    isDeactvating: boolean;
}

const membersInitialValue: IMembersState = {
    payload: {
        members: [],
        next_page_token: null,
    },
    isFetching: false,
    error: null,
};

const memberInitialValue: IMemberState = {
    payload: null,
    isDeactvating: false,
    error: null,
};

export const merchantAdminMemberList = combineReducers<IMerchantAdminMemberListState>({
    members: handleActions(
        {
            ...updateActions(MERCHANT_ADMIN_MEMBER_LIST.MEMBERS),
            ...progressActions(MERCHANT_ADMIN_MEMBER_LIST.MEMBERS.FETCH, "isFetching"),
            [MERCHANT_ADMIN_MEMBER_LIST.MEMBERS.FETCH.SUCCESS]: (
                state: IMembersState,
                action: Action<IMembersPayload>,
            ) => {
                const { members, next_page_token } = action.payload;

                if (!Array.isArray(members)) {
                    return {
                        isFetching: false,
                        error: format("Received a malformed response while fetching members"),
                    };
                }

                const allMembers = state.payload.members.slice(0, -1).concat(members);

                if (!_.isEmpty(next_page_token)) {
                    allMembers.push(null);
                }

                return {
                    payload: {
                        members: allMembers,
                        next_page_token,
                    },
                    isFetching: false,
                };
            },
            [MERCHANT_ADMIN_MEMBER_LIST.MEMBER.DEACTIVATE.SUCCESS]: (
                state: IMembersState,
                action: ActionMeta<IMembersPayload, { member_id: string }>,
            ) => {
                const {
                    payload: { members },
                } = state;
                const index = _.findIndex<IMemberWithBasicProfile>(
                    (member) => member?.member?.member_id === action.meta?.member_id,
                )(members);
                return _.set(["payload", "members", index, "member", "deactivated"])(true)(state);
            },
            [MERCHANT_ADMIN_MEMBER_LIST.MEMBER.ACTIVATE.SUCCESS]: (
                state: IMembersState,
                action: ActionMeta<IMembersPayload, { member_id: string }>,
            ) => {
                const {
                    payload: { members },
                } = state;
                const index = _.findIndex<IMemberWithBasicProfile>(
                    (member) => member?.member?.member_id === action.meta?.member_id,
                )(members);
                return _.set(["payload", "members", index, "member", "deactivated"])(false)(state);
            },
            [MERCHANT_ADMIN_MEMBER_LIST.MEMBERS.RESET]: () => membersInitialValue,
        } as any,
        membersInitialValue,
    ),
    member: handleActions(
        {
            ...progressActions(MERCHANT_ADMIN_MEMBER_LIST.MEMBER.DEACTIVATE, "isDeactvating"),
        } as any,
        memberInitialValue,
    ),
    successMessage: handleActions<string>(
        {
            [MERCHANT_ADMIN_MEMBER_LIST.SUCCESS_MESSAGE.SET]: (_, action) => action.payload,
            [MERCHANT_ADMIN_MEMBER_LIST.SUCCESS_MESSAGE.CLEAR]: () => null,
        },
        null,
    ),
    errorMessage: handleActions<string>(
        {
            [MERCHANT_ADMIN_MEMBER_LIST.ERROR_MESSAGE.SET]: (_, action) => action.payload,
            [MERCHANT_ADMIN_MEMBER_LIST.ERROR_MESSAGE.CLEAR]: () => null,
        },
        null,
    ),
    addingAdminMembers: handleActions<boolean>(
        {
            [MERCHANT_ADMIN_MEMBER_LIST.MEMBERS.ADD.START]: () => true,
            [MERCHANT_ADMIN_MEMBER_LIST.MEMBERS.ADD.SUCCESS]: () => false,
        },
        null,
    ),
});
